/* eslint-disable react-hooks/exhaustive-deps */
import AuditSheet from './components/AuditSheet';
import AuditSheetFooter from './components/AuditSheetFooter';
import Topbar from './components/Topbar';
import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  useBoolean,
  VStack,
  Text
} from '@chakra-ui/react';
import AppColors from '../../constants/AppColors';
import { withProvider } from '../../hoc/withProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AuditSheetProvider, {
  AuditSheetContext,
} from '../../providers/AuditSheetProvider';
import { createEmptyObject } from './components/createEmptyObject';
import { sanitize } from './components/sanitize';
import SubmitModal from './components/SubmitModal';
import ROUTES from '../../constants/Routes';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { successToast } from '../../components/successToast';
import { submitRule } from './components/submitRules';
import CommentBox from './components/CommentBox';
import html2canvas from 'html2canvas';
import {
  arrangeData,
  createEmptyColumn,
  prepareUpdatePayload,
} from '../../utils/auditsheet.utils';
import { AuthContext } from '../../providers/AuthProvider';
import { isSheetEditableBy } from './sheetAccess';
import { NAVIGATE_AWAY } from './components/BatchChartNavigator';
import { AccessDenied } from './components/AccessDenied';

const AuditSheetContainer = ({ view, batch}) => {
  const auditSheetCount = Math.ceil(window.innerHeight / 24);
  const location = useLocation();

  // refs
  const domEl = useRef(null);
  const topbar = useRef(null);
  const footer = useRef(null);
  const saveInProgressRef = useRef(false);
  const notesDrawerRef = useRef(null);

  // Booleans
  const [isLoading, ilState] = useBoolean(false);
  const [isOpen, ioState] = useBoolean(false);
  const [isCommentOpen, icoState] = useBoolean(false);

  // other hooks
  const { uploadId, batchId } = useParams(); // chartId
  const navigate = useNavigate();
  const toast = useToastr();

  // states
  const [lastSaved, setLastSaved] = useState('');
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [sheets, setSheets] = useState([
    {
      index: 1,
      sheet_name: 'Sheet1',
      data: createEmptyObject(auditSheetCount),
    },
  ]);
  // Always 0: a chart has exactly one sheet now that the footer sheet tabs are
  // gone, so there is nothing left that can switch the active sheet.
  const [activeSheetIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [metaData, setMetaData] = useState({});
  const [isSubmitDisabled, setSubmitDisabled] = useState(true);
  const [batchName, setBatchName] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  //context
  const auditSheetContext = useContext(AuditSheetContext);
  const authContext = useContext(AuthContext)
  const {
    getAuditUpload,
    getAuditSheet,
    currentUpload,
    auditSheetColumnsKeys,
    getBatchAuditSheet,
    isAccessDenied,
    isAuditSheetLoading
  } = auditSheetContext;
  const { getIndustryCodes, getICDCodes, getModifiers, getUsers, users } = auditSheetContext;
  const { getAllUsers, getCommentFlag, providerOptions } = auditSheetContext;
  const { getComments, setSheetData, getRebuttalSubmitValidation, allComments, setChartBatchId, chartBatchId } = auditSheetContext;
  const [updatingAuditSheet, uasState] = useBoolean(false);

  // get auditsheet user data
  const getUpload = async () => {
    if (batch) {
      if (!view) {
        navigate(`${location.pathname}/view`, { replace: true });
        view = true;
      }
      getSheet();
      return;
    }
    if (uploadId) {
      const res = await getAuditUpload(uploadId);
      if (res.error) {
        setErrMsg(res.error);
        return;
      }
      setLastSaved(res.updated_date);
      if (!view) {     
        // Anything this user cannot edit -- read only status, or a chart assigned
        // to another auditor -- opens on the view only route instead.
        if (!isSheetEditableBy(res, authContext.currentUser)) {
          navigate(`${location.pathname}/view`, { replace: true });
          view = true;
          return;
        }
        auditSheetContext.monitorAuditHour(uploadId);
      }
    }
  };

  // Build sheetData map: { rowId: rowNumber (1-based) } for location display in notes drawer
  useEffect(() => {
    const map = {};
    sheets.forEach(sheet => {
      sheet.data.forEach((row, idx) => {
        if (row.id != null) {
          map[row.id] = idx + 1;
        }
      });
    });
    setSheetData(map);
  }, [sheets]);

  const onCommentClick = async (rowIndex, rowData) => {
    if (!rowData?.id) return;
    setMetaData({ rowIndex, selectedRowData: rowData });
    await getComments(uploadId, { row: rowData.id });
    icoState.on();
  };

  const onSelectionEnd = (rowData) => {
    setSelectedRowData(rowData);
  };

  const onZoom = value => {
    let temp = 1;
    if (value >= 50) {
      temp = (2 * value) / 100;
    } else if (value === 0) {
      temp = 0.3;
    } else {
      temp = (value / 100) * 2;
    }
    setScale(temp);
  };

  // sheets Data || fetch data
  const getSheet = async () => {
    if (!isAccessDenied || batch) {
      if (uploadId) {
        const response = await getAuditSheet(uploadId, currentUpload);
        const sheetBatchId = response[0]?.batch_id || response[0]?.batch;
        if (sheetBatchId) setChartBatchId(sheetBatchId);
        
        if (response?.length) {
          const updated = response.map((res, i) => {
            const _reDestructuredData = createEmptyColumn(res.data);

            const reDestructuredData = _reDestructuredData.map(sd => {
              const tempObj = { ...sd, ...sd.additional_attributes };
              tempObj['audited_cpt'] = tempObj['audited_code'];
              delete tempObj.additional_attributes;
              delete tempObj?.audited_code;

              if (tempObj?.enc_dt) {
                  const parts = tempObj.enc_dt.split('-');
                  const year = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10) - 1;
                  const day = parseInt(parts[2], 10);
                  const parsedDate = new Date(year, month, day);
            
                  if (!isNaN(parsedDate.getTime())) {
                    const formattedDate = `${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}-${parsedDate.getDate().toString().padStart(2, '0')}-${parsedDate.getFullYear()}`;
                    tempObj.enc_dt = formattedDate;
                  }
              }
              if (!tempObj.hasOwnProperty('icd_agree')){
                tempObj['icd_agree'] = null
              }
              if (!tempObj.hasOwnProperty('icd_disagree')){
                tempObj['icd_disagree'] = null
              }

              return tempObj;
            });

            const _arrangeData = arrangeData(reDestructuredData);

            const updatedData = [
              ..._arrangeData,
              ...createEmptyObject(auditSheetCount - res.data.length),
            ];
            return {
              ...res,
              index: i + 1,
              data: updatedData,
            };
          });
          setSheets(updated);
        }
      } else if (batch) {
          const response = await getBatchAuditSheet(batchId);
          if (response?.error) {
            setErrMsg(response.error);
            return;
          }
    
          if (response.length) {
            setBatchName(response[0].batch_name);
            const updated = response.map((res, i) => {
    
              const _reDestructuredData = createEmptyColumn(res.data);
    
              const reDestructuredData = _reDestructuredData.map(sd => {
                const tempObj = { ...sd, ...sd.additional_attributes };
                tempObj['audited_cpt'] = tempObj['audited_code'];
                delete tempObj.additional_attributes;
                delete tempObj?.audited_code;
    
                if (tempObj?.enc_dt) {
                  const parts = tempObj.enc_dt.split('-');
                  const year = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10) - 1;
                  const day = parseInt(parts[2], 10);
                  const parsedDate = new Date(year, month, day);
    
                  if (!isNaN(parsedDate.getTime())) {
                    const formattedDate = `${(parsedDate.getMonth() + 1)
                      .toString()
                      .padStart(2, '0')}-${parsedDate
                      .getDate()
                      .toString()
                      .padStart(2, '0')}-${parsedDate.getFullYear()}`;
                    tempObj.enc_dt = formattedDate;
                  }
                }
                if (!tempObj.hasOwnProperty('icd_agree')){
                  tempObj['icd_agree'] = null
                }
                if (!tempObj.hasOwnProperty('icd_disagree')){
                  tempObj['icd_disagree'] = null
                }
                return tempObj;
              });
    
    
              const _arrangeData = arrangeData(reDestructuredData);
    
    
              const updatedData = [
                ..._arrangeData,
                ...createEmptyObject(auditSheetCount - res.data.length),
              ];
              return {
                ...res,
                index: i + 1,
                data: updatedData,
              };
            });
    
            setSheets(updated);
            disableSubmitFunc(response)
    
          }
        };
      }
  };

  useEffect(() => {
    if(!isAccessDenied){
      auditSheetContext.fetchResponseAuditIssues();
    }
    if(!isAccessDenied && !view){
      getIndustryCodes();
      getICDCodes();
      getModifiers();
      getUsers()
      getAllUsers()
      if (uploadId) {
        auditSheetContext.fetchProviderDropdown(uploadId)
      }
    }
  }, [isAccessDenied]);

  useEffect(() => {
    if(!isAccessDenied){
      if (uploadId || batchId) {
        getCommentFlag({ uploadId, batchId })
      }
    }
  }, [uploadId, batchId, isAccessDenied])

  useEffect(() => {
      getUpload();
    }, []);

  useEffect(()=>{
    // performing get sheet for single audit sheet view because batch already has their sheet called
    if (!isAccessDenied && uploadId) {
      getSheet();
    }

  },[uploadId, isAccessDenied])

  useEffect(() => {
    /**
     * sanitize() will add old_rendering and asign as id to rendering and remove empty rows
     */

    const sanitizedSheet = sanitize(sheets, providerOptions);

    disableSubmitFunc(sanitizedSheet);
  }, [sheets]);

  // functions and handlers

  function disableSubmitFunc(sanitizedSheet) {
    // For REBUTTAL IN PROGRESS, skip data validation — audit was already validated
    // when first submitted. Auditor/manager just needs to respond to rebuttals and re-submit.
    if (currentUpload?.status === 'REBUTTAL IN PROGRESS') {
      setSubmitDisabled(false);
      return;
    }

    const isEverySanitizedSheetEmpty = sanitizedSheet.every(
      sheet => sheet.data.length === 0
    );

    if (isEverySanitizedSheetEmpty) {
      setSubmitDisabled(true);
    } else {
      const isSubmitable = submitRule(sanitizedSheet);
      setSubmitDisabled(isSubmitable);
    }
  }

  // An autosave is running or queued behind one. The footer navigator stays
  // locked for that window so nobody leaves a sheet mid-save.
  const isSheetSaving = updatingAuditSheet;

  /**
   * Flushes pending work before the footer navigator leaves this sheet, the same
   * way the topbar back button does, and reports whether it is safe to go.
   *
   * Anything but OK keeps the navigator where it is -- losing an edit is far
   * worse than a click that does not navigate.
   */
  async function onNavigateAwayFromSheet() {
    // Nothing editable on screen, so nothing to lose.
    if (view || batch || !uploadId) return NAVIGATE_AWAY.OK;
    // Saving again now would only queue behind the running save, so it could
    // not be confirmed before we leave. Ask the user to wait for it instead.
    if (isSheetSaving) return NAVIGATE_AWAY.SAVING;
    ilState.on();
    try {
      return (await updateAuditSheet()) ? NAVIGATE_AWAY.OK : NAVIGATE_AWAY.FAILED;
    } catch (err) {
      return NAVIGATE_AWAY.FAILED;
    } finally {
      ilState.off();
    }
  }

  function updateSheet(sheetData) {
    sheets[activeSheetIndex].data = sheetData;
    setSheets([...sheets]);
  }

  const removeRows = rowIndexes => {
    const sortedIndexes = [...rowIndexes].sort((a, b) => b - a); // bottom-up so indices stay correct
    const data = [...sheets[activeSheetIndex].data];
    sortedIndexes.forEach(i => data.splice(i, 1));
    sheets[activeSheetIndex].data = data;
    setSheets([...sheets]);
  };

  function onOpenNotes(obj) {
    if (obj && Object.keys(obj).length) {
      if (obj.all) {
        delete auditSheetContext.commentFilters.sheet_name;
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          page: 1,
        });
      } else if (obj.current) {
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          sheet_name: sheets[activeSheetIndex].sheet_name,
          page: 1,
        });
      }
    } else {
      auditSheetContext.setCommentFilters({
        ...auditSheetContext.commentFilters,
        uploadId,
        batchId,
        sheet_name: sheets[activeSheetIndex].sheet_name,
        reverse: true,
        page: 1,
      });
    }
  }

  // async functions

  async function downloadImage() {
    // download image
    function dataURIToBlob(dataURI) {
      const splitDataURI = dataURI.split(',');
      const byteString =
        splitDataURI[0].indexOf('base64') >= 0
          ? atob(splitDataURI[1])
          : decodeURI(splitDataURI[1]);
      const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

      const ia = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++)
        ia[i] = byteString.charCodeAt(i);

      return new Blob([ia], { type: mimeString });
    }
    const canvas = await html2canvas(domEl.current);
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    return dataURIToBlob(dataUrl);
  }

  const updateCurrentAuditImage = async () => {
    try {
      //submit screenshot
      const file = await downloadImage();
      const fd = new FormData();
      fd.append('image', file);
      return await auditSheetContext.recentAuditImageUpload(uploadId, fd);
    } catch (err) {

    }
  }

  async function onAuditSubmit() {
    if (saveInProgressRef.current) return;
    saveInProgressRef.current = true;
    uasState.on();
    ilState.on();
    try {
    // await auditSheetContext.monitorAuditHour(uploadId);
    await updateCurrentAuditImage();
    const sanitizedSheet = sanitize(sheets, providerOptions);

    const payload = prepareUpdatePayload(sanitizedSheet);

    //  const payload= payloadreStructure(sanitizedSheet)

    const response = await auditSheetContext.updateAuditSheet(
      payload, //sanitizedSheet,
      uploadId
    );

    if (response) {
      ioState.off();
      navigate(ROUTES.MY_AUDITS);
      const toastProps = {
        header: 'Success!',
        description: `${'Document submitted for review.'}`,
      };
      successToast(toast, toastProps);
    }
    } finally {
      saveInProgressRef.current = false;
      uasState.off();
      ilState.off();
    }
  }

  const prepareDeltaPayload = (sheet, key) => {
    if (!sheet[key]?.length) return sheet[key];

    // 1️Sanitize
    const sanitized = sanitize(
      [{ ...sheet, data: sheet[key] }],
      providerOptions
    );
    // Prepare payload (same structure as sanitize)
    const payloadResult = prepareUpdatePayload(sanitized);
    // since both return array of sheets
    return payloadResult[0].data;
  };

  /**
   *
   * @param {Array} sheetParams
   * @returns
   */

  // Returns true only when the sheet is known to be saved. Callers that are
  // about to discard this sheet (the footer navigator) rely on that; callers
  // that only autosave ignore it.
  async function updateAuditSheet(sheetParams) {
    // Exclude overlapping callers synchronously, including calls from the same render.
    // A rejected autosave stays in the grid's pending queue.
    if (saveInProgressRef.current) return false;
    saveInProgressRef.current = true;
    uasState.on();
    try {
      if (sheetParams?.deltaPayload) {
        const delta = sheetParams.deltaPayload;
        const payload = {
          ...delta,
          created: prepareDeltaPayload(delta, 'created'),
          updated: prepareDeltaPayload(delta, 'updated'),
        };
        if (payload.created.length !== delta.created.length) {
          throw new Error('A new row could not be prepared for saving.');
        }
        const response = await auditSheetContext.createAuditSheetDelta(payload, uploadId);
        setLastSaved(new Date());
        return response;
      }

      // Block editing while a full replacement is saved and reloaded.
      ilState.on();
      if (sheetParams) sheets[activeSheetIndex] = sheetParams;
      const payload = prepareUpdatePayload(sanitize(sheets, providerOptions));
      await auditSheetContext.createAuditSheet(payload, uploadId);
      await getSheet();
      await updateCurrentAuditImage();
      setLastSaved(new Date());
      return true;
    } catch (err) {
      console.error('Audit sheet save failed:', err);
      if (sheetParams?.deltaPayload) throw err;
      return false;
    } finally {
      saveInProgressRef.current = false;
      uasState.off();
      ilState.off();
    }
  }


  // Adding, renaming, deleting and switching sheets lived here to serve the
  // footer sheet tabs. Those are gone and nothing else reaches this, so every
  // chart keeps the single "Sheet1" it is created with. `sheet_name` and `index`
  // are still sent on every update, so the backend contract is untouched.


  const [coords, setCoords] = useState({
    x: 0,
    y: 0,
  });

  function handleSheetClick(e) {
    setCoords({
      x:e.clientX,
      y:e.clientY
    })
  }

  // renders
  const renderComment = () => {
    return (
      <CommentBox
        showMenu={isCommentOpen}
        metaData={metaData}
        closeComment={() => icoState.off()}
        coords={coords}
      />
    );
  };

  const renederToolBar = () => {
    return (
      <Box w={'100%'} ref={topbar} top={0}>
        <Topbar
          onAuditSubmit={async () => {
            if (currentUpload?.status === 'REBUTTAL IN PROGRESS') {
              const hasOpenRebuttals = allComments.some(c => {
                if (c.parent || c.action !== 'REBUTTAL') return false;
                const replies = c.reply_comments || [];
                let status = 'Open';
                for (const r of replies) {
                  if (r.action === 'REBUTTAL-REJECTED') status = 'Rejected';
                  else if (r.action === 'REBUTTAL-RESOLVED') status = 'Closed';
                  else if (r.action === 'REBUTTAL-ACCEPTED') status = 'Accepted';
                  else if (r.action === 'RE-OPENED') status = 'Accepted';
                }
                return status === 'Accepted';
              });
              if (hasOpenRebuttals) {
                toast.showError({ description: 'Please close all accepted rebuttals before submitting.' });
                return;
              }
              const validation = await getRebuttalSubmitValidation(uploadId);
              if (!validation?.can_submit) {
                toast.showError({ description: validation?.message || 'Close all accepted rebuttals before submitting.' });
                return;
              }
            }
            ioState.on();
          }}
          batchId={batchId}
          batchName={batchName}
          sheet={sheets[activeSheetIndex]}
          updateAuditSheet={async params => {
            return await updateAuditSheet(params);
          }}
          view={view}
          disableSubmit={isSubmitDisabled || updatingAuditSheet}
          lastSaved={lastSaved}
          onChange={on => {
            if (on) {
              ilState.on();
            } else {
              ilState.off();
            }
          }}
          onOpenNotes={onOpenNotes}
          notesDrawerRef={notesDrawerRef}
        />
      </Box>
    );
  };





  const renderAuditSheet = () => (
    <Box id="domEl" ref={domEl} onClick={handleSheetClick}>
      <AuditSheet
        height={
          window.innerHeight -
          topbar?.current?.clientHeight -
          footer?.current?.clientHeight
        }
        scale={scale}
        sheet={sheets[activeSheetIndex]}
        uploadId={uploadId}
        view={view}
        batch={batch}
        updateAuditSheet={updateAuditSheet} // create audit sheet(POST) and getSheets and update sheet
        updateSheet={updateSheet} // update the data in setState(setSheet)
        onCommentClick={onCommentClick}
        onSelectionEnd={onSelectionEnd}
        removeRows={removeRows}
        closeComment={() => icoState.off()}
        getSheet={getSheet}
      />
    </Box>
  );

  const renderFooter = () => {
    return (
      <Box w={'100%'} ref={footer} position={'fixed'} bottom={0}>
        <AuditSheetFooter
          onZoom={onZoom}
          batchId={batchId || currentUpload?.batch_id || chartBatchId}
          currentChartId={uploadId}
          isBatchView={!!batch}
          onNavigateAway={onNavigateAwayFromSheet}
          isSaving={isSheetSaving}
        />
      </Box>
    );
  };

  const renderSubmitModal = () => {
    return (
      <SubmitModal
        isOpen={isOpen}
        onClose={() => ioState.off()}
        onAuditSubmit={onAuditSubmit}
      />
    );
  };

  const renderLoader = () => {
    return (
      <Modal size={'sm'} isOpen={isLoading} isCentered bgColor={'transparent'}>
        <ModalOverlay style={{ backgroundColor: AppColors.lightSkyBlue }} />
        <ModalContent bgColor={'transparent'} shadow={'none'}>
          <ModalBody style={{ alignSelf: 'center' }}>
            <Spinner />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

  const renderMainContent = () => {
    return (
      (isAuditSheetLoading ? (
            <VStack height="100vh" justify="center" align="center">
              <Spinner size="xl" />
              <Text>Loading...</Text>
            </VStack>
          ) :
          (!isAccessDenied ? (
      <Box bgColor={AppColors.bgColor}>
        {renederToolBar()}

        {renderAuditSheet()}

        {renderFooter()}

        {renderSubmitModal()}

        {renderLoader()}

        {renderComment()}
      </Box>
          ): <AccessDenied
                  errMsg={errMsg}/>
        ))
    );
  };

  return renderMainContent();
};
export default withProvider(AuditSheetProvider, AuditSheetContainer);